import { useEffect } from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from './Button';
import FormInput from './FormInput';
import Modal from './Modal';
import { useTranslation } from 'react-i18next';
import i18n from '../common/config/i18n';
import { IProfile, IProfileCreateDTO } from '../common/interfaces/profile.interface';
import Toggle from './Toggle';
import Select from './Select';
import { UserRoleEnum } from '../common/interfaces/user-profile.interface';

interface ProfileModalProps {
  title: string;
  onClose: () => void;
  onSubmit: (profile: IProfileCreateDTO) => void;
  buttonLabel: string;
  isLoading?: boolean;
  profile?: IProfile | null;
}

const AVAILABLE_ROLES = [UserRoleEnum.ADMIN, UserRoleEnum.EMPLOYEE].map((role) => ({
  key: role,
  value: i18n.t(`roles:${role}`),
}));

const schema = yup
  .object({
    email: yup
      .string()
      .email(i18n.t('profile_modal:email.errors.invalid'))
      .required(i18n.t('profile_modal:email.errors.required')),
    phone: yup.string().notRequired(),
    details: yup.string().notRequired(),
    role: yup.mixed().required(i18n.t('profile_modal:role.errors.required')),
    status: yup.string().notRequired(),
  })
  .required();

const ProfileModal = ({
  title,
  onClose,
  onSubmit,
  buttonLabel,
  profile,
  isLoading,
}: ProfileModalProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<any>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  const { t } = useTranslation('profile_modal');

  useEffect(() => {
    if (profile) {
      reset({ ...profile, role: { key: profile.role, value: i18n.t(`roles:${profile.role}`) } });
    } else {
      reset({
        role: { key: UserRoleEnum.EMPLOYEE, value: i18n.t(`roles:${UserRoleEnum.EMPLOYEE}`) },
      });
    }
  }, [profile]);

  return (
    <Modal title={title} onClose={onClose}>
      <div className="flex flex-col px-12 w-full gap-[60px]">
        <form className="gap-4" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            key="email"
            name="email"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <FormInput
                  type="email"
                  errorMessage={errors.email?.message?.toString()}
                  value={value}
                  label={t('email.label')}
                  onChange={onChange}
                  helper={t('email.helper')}
                  disabled={profile ? true : false}
                />
              );
            }}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <FormInput
                  type="tel"
                  errorMessage={errors.phone?.message?.toString()}
                  value={value}
                  label={t('phone.label')}
                  onChange={onChange}
                  helper={t('phone.helper')}
                />
              );
            }}
          />
          <Controller
            name="details"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <FormInput
                  type="details"
                  errorMessage={errors.details?.message?.toString()}
                  value={value}
                  label={t('details.label')}
                  onChange={onChange}
                  helper={t('details.helper')}
                />
              );
            }}
          />
          <Controller
            name="role"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <>
                  <Select
                    placeholder={`Rolul utilizatorului`}
                    options={AVAILABLE_ROLES}
                    onChange={onChange}
                    selected={value}
                  />
                  {errors.role?.message && (
                    <p className="text-red-500">{errors.role?.message?.toString()}</p>
                  )}
                </>
              );
            }}
          />
          {profile && (
            <Controller
              key="status"
              name="status"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <Toggle
                    id="profile_status__toggle"
                    label={t('status.label')}
                    enabledText={t('status.active')}
                    disabledText={t('status.inactive')}
                    onChange={onChange}
                    value={value}
                  />
                );
              }}
            />
          )}
        </form>
        <div className="flex flex-row-reverse">
          <Button
            id="profile_form__submit-button"
            label={buttonLabel}
            className="btn-primary w-full"
            onClick={handleSubmit(onSubmit)}
            loading={isLoading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ProfileModal;
